// `.cube` LUT parser (§11.6): 1D and 3D, LUT_1D_SIZE / LUT_3D_SIZE up to 65, DOMAIN_MIN / DOMAIN_MAX,
// TITLE, comments. Values are in the encoded (display) domain. Data order: red varies fastest.
export type CubeLut = {
  title: string;
  kind: '1d' | '3d';
  size: number;
  domainMin: [number, number, number];
  domainMax: [number, number, number];
  /** RGB triples: size entries (1D) or size³ entries (3D), red fastest. */
  data: Float32Array;
};

export class CubeError extends Error {}

export const MAX_CUBE_SIZE = 65;

export function parseCube(text: string): CubeLut {
  let title = '';
  let kind: '1d' | '3d' | null = null;
  let size = 0;
  let domainMin: [number, number, number] = [0, 0, 0];
  let domainMax: [number, number, number] = [1, 1, 1];
  const values: number[] = [];
  const triple = (parts: string[], line: number): [number, number, number] => {
    const v = parts.slice(1, 4).map(Number);
    if (v.length !== 3 || v.some((x) => !Number.isFinite(x))) throw new CubeError(`line ${line}: bad triple`);
    return v as [number, number, number];
  };
  const lines = text.split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i]!.replace(/#.*/, '').trim();
    if (!raw) continue;
    const parts = raw.split(/\s+/);
    const key = parts[0]!.toUpperCase();
    if (key === 'TITLE') {
      title = raw.slice(5).trim().replace(/^"|"$/g, '');
    } else if (key === 'LUT_3D_SIZE' || key === 'LUT_1D_SIZE') {
      if (kind) throw new CubeError(`line ${i + 1}: more than one LUT size`);
      kind = key === 'LUT_3D_SIZE' ? '3d' : '1d';
      size = Number(parts[1]);
      const max = kind === '3d' ? MAX_CUBE_SIZE : 65536;
      if (!Number.isInteger(size) || size < 2 || size > max)
        throw new CubeError(`line ${i + 1}: size ${parts[1]} out of range (2..${max})`);
    } else if (key === 'DOMAIN_MIN') domainMin = triple(parts, i + 1);
    else if (key === 'DOMAIN_MAX') domainMax = triple(parts, i + 1);
    else if (key === 'LUT_1D_INPUT_RANGE' || key === 'LUT_3D_INPUT_RANGE') {
      const lo = Number(parts[1]),
        hi = Number(parts[2]);
      domainMin = [lo, lo, lo];
      domainMax = [hi, hi, hi];
    } else if (/^[-+.\d]/.test(key)) {
      if (parts.length < 3) throw new CubeError(`line ${i + 1}: expected 3 numbers`);
      for (let k = 0; k < 3; k++) {
        const v = Number(parts[k]);
        if (!Number.isFinite(v)) throw new CubeError(`line ${i + 1}: not a number`);
        values.push(v);
      }
    }
    // Other keywords (e.g. LUT_IN_VIDEO_RANGE) are ignored.
  }
  if (!kind) throw new CubeError('missing LUT_3D_SIZE or LUT_1D_SIZE');
  const n = kind === '3d' ? size ** 3 : size;
  if (values.length !== n * 3) throw new CubeError(`expected ${n} entries, found ${values.length / 3}`);
  for (let c = 0; c < 3; c++)
    if (!(domainMax[c]! > domainMin[c]!)) throw new CubeError('DOMAIN_MAX must exceed DOMAIN_MIN');
  return { title, kind, size, domainMin, domainMax, data: Float32Array.from(values) };
}

/** CPU reference lookup (trilinear for 3D, linear per channel for 1D) of an encoded RGB value. */
export function applyCube(l: CubeLut, rgb: [number, number, number]): [number, number, number] {
  const u = rgb.map((v, c) =>
    Math.min(1, Math.max(0, (v - l.domainMin[c]!) / (l.domainMax[c]! - l.domainMin[c]!))),
  ) as [number, number, number];
  const N = l.size;
  const d = l.data;
  if (l.kind === '1d') {
    return u.map((v, c) => {
      const x = v * (N - 1);
      const i = Math.min(N - 2, Math.floor(x));
      const t = x - i;
      return d[i * 3 + c]! + (d[(i + 1) * 3 + c]! - d[i * 3 + c]!) * t;
    }) as [number, number, number];
  }
  const f = u.map((v) => v * (N - 1));
  const i0 = f.map((v) => Math.min(N - 2, Math.floor(v)));
  const t = f.map((v, k) => v - i0[k]!);
  const at = (r: number, g: number, b: number, c: number) => d[((b * N + g) * N + r) * 3 + c]!;
  const out: [number, number, number] = [0, 0, 0];
  for (let c = 0; c < 3; c++) {
    let acc = 0;
    for (let k = 0; k < 8; k++) {
      const dr = k & 1,
        dg = (k >> 1) & 1,
        db = (k >> 2) & 1;
      const w = (dr ? t[0]! : 1 - t[0]!) * (dg ? t[1]! : 1 - t[1]!) * (db ? t[2]! : 1 - t[2]!);
      acc += w * at(i0[0]! + dr, i0[1]! + dg, i0[2]! + db, c);
    }
    out[c] = acc;
  }
  return out;
}

/** An identity LUT as `.cube` text (tests and fixtures). */
export function identityCube(size = 17, kind: '1d' | '3d' = '3d'): string {
  const lines = [`TITLE "Identity ${size}"`, kind === '3d' ? `LUT_3D_SIZE ${size}` : `LUT_1D_SIZE ${size}`];
  const v = (i: number) => (i / (size - 1)).toFixed(6);
  if (kind === '1d') for (let i = 0; i < size; i++) lines.push(`${v(i)} ${v(i)} ${v(i)}`);
  else
    for (let b = 0; b < size; b++)
      for (let g = 0; g < size; g++) for (let r = 0; r < size; r++) lines.push(`${v(r)} ${v(g)} ${v(b)}`);
  return lines.join('\n') + '\n';
}
